import { Box, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import PivotablePieChart from "../components/chart/PivotablePieChart";
import { M3Surface } from "../components/m3";
import { getOrgTokens } from "../theme/style/organizationPalette";

// Chart cards fill their grid cell; the chart draws its own title bar.
const CHART_SURFACE_STYLE = { height: "100%", overflow: "hidden" };

const getMciStatement = (topic = "Product") => {
  const entity = topic.toLowerCase();
  return `This graph illustrates the Material Circularity Indicator (MCI), derived strictly from the Ellen MacArthur Foundation (EMF) methodology. It provides a linear, mass-balance measurement of the ${entity}’s physical material flows. The calculation tracks the percentage of virgin versus recycled or reused input materials entering production, coupled with the efficiency and destination of materials at the end of the ${entity}'s life cycle. By focusing on mass fractions, it quantifies how effectively the physical matter is kept out of landfills and retained within a closed-loop system.`;
};

const CircularChart = ({ setSelectedEnvFactorID, topic = "Product", title, combined_data, showDescription = true }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const defaultTitle = t("Material Circularity Indicator");
  const effectiveTitle = title ? t(title) : defaultTitle;

  const percentage_product_mci = useMemo(() => combined_data?.percentage_product_mci || 0, [combined_data?.percentage_product_mci]);
  const circularities = useMemo(() => combined_data?.combined_circularities || [], [combined_data?.combined_circularities]);
  const chartData = useMemo(() => circularities.map((row) => ({ label: row.description, value: row.mci_contribution })), [circularities]);

  const statement = useMemo(() => getMciStatement(topic), [topic]);

  const sideContent = showDescription ? (
    <Typography
      sx={{
        fontFamily: tok.font.family,
        fontSize: "0.78rem",
        lineHeight: 1.55,
        color: tok.text.secondary,
        textAlign: "left",
      }}
    >
      {t(statement)}
    </Typography>
  ) : null;

  return (
    <M3Surface style={CHART_SURFACE_STYLE}>
      <PivotablePieChart
        title={effectiveTitle}
        usages={chartData}
        groupBy={"label"}
        countBy={"value"}
        hollow
        SideContent={sideContent}
        collapsibleSideContent
        sideContentDefaultOpen={false}
        InnerTextComponent={
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", height: 1, width: "100%", px: 1, textAlign: "center" }}>
            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "11px",
                fontWeight: 500,
                lineHeight: 1.25,
                color: tok.text.secondary,
                textAlign: "center",
                maxWidth: "140px",
                whiteSpace: "normal",
              }}
            >
              {t("Material Circularity Indicator")}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "20px",
                fontWeight: 600,
                color: tok.text.primary,
                textAlign: "center",
                mt: 0.5,
              }}
            >
              {`${(percentage_product_mci * 100).toFixed(2)}%`}
            </Typography>
          </Box>
        }
        onClick={(item) => {
          if (setSelectedEnvFactorID) {
            const envId = circularities.find((row) => row.description === item.name)?.env_id;
            if (envId) setSelectedEnvFactorID(envId);
          }
        }}
      />
    </M3Surface>
  );
};

export default CircularChart;
